import { action, makeObservable, observable, runInAction } from 'mobx';
import { directoryAxiosClient } from 'Services/Api/client';
import DIRECTORY_API from 'Services/Api/Directory/Api';
import {
	DirectoryGroup,
	DirectoryRole,
	DirectoryUser
} from 'Services/Api/Directory/Types';
import { Status } from 'app-types';

class AssignUsersStore {
	status = {
		fetchOrganizations: Status.Idle,
		fetchProperties: Status.Idle,
		fetchRoles: Status.Idle,
		fetchGroups: Status.Idle,
		fetchUsers: Status.Idle,
		fetchUsersByIds: Status.Idle
	};

	organizations: DirectoryGroup[] = [];
	properties: DirectoryGroup[] = [];
	roles: DirectoryRole[] = [];
	groups: DirectoryGroup[] = [];
	users: DirectoryUser[] = [];
	// Isolated from `users` so the create-flow property picker is never affected.
	assignmentUsers: DirectoryUser[] = [];

	constructor() {
		makeObservable(this, {
			status: observable,
			organizations: observable,
			properties: observable,
			roles: observable,
			groups: observable,
			users: observable,
			assignmentUsers: observable
		});
	}

	@action
	async fetchOrganizations(): Promise<void> {
		runInAction(() => {
			this.status.fetchOrganizations = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?label[]=Organization`
			);
			runInAction(() => {
				this.organizations = data;
				this.status.fetchOrganizations = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchOrganizations = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchProperties(): Promise<void> {
		runInAction(() => {
			this.status.fetchProperties = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?label[]=Property`
			);
			runInAction(() => {
				this.properties = data;
				this.status.fetchProperties = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchProperties = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchRoles(): Promise<void> {
		runInAction(() => {
			this.status.fetchRoles = Status.Pending;
		});
		try {
			const { data } = await directoryAxiosClient.get<DirectoryRole[]>(
				DIRECTORY_API.ROLES
			);
			runInAction(() => {
				this.roles = data;
				this.status.fetchRoles = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchRoles = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchGroupsByProperty(propertyIds: number[]): Promise<void> {
		if (!propertyIds.length) {
			runInAction(() => {
				this.groups = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchGroups = Status.Pending;
		});
		try {
			const ids = propertyIds.join(',');
			const { data } = await directoryAxiosClient.get<DirectoryGroup[]>(
				`${DIRECTORY_API.GROUPS}?id=${ids}&include_group_tree=true&label[]=Group`
			);
			runInAction(() => {
				this.groups = data;
				this.status.fetchGroups = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchGroups = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchUsersByProperty(propertyIds: number[]): Promise<void> {
		if (!propertyIds.length) {
			runInAction(() => {
				this.users = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchUsers = Status.Pending;
		});
		try {
			const ids = propertyIds.join(',');
			const { data } = await directoryAxiosClient.get<DirectoryUser[]>(
				`${DIRECTORY_API.USERS}?group_id=${ids}&include_group_tree=true`
			);
			runInAction(() => {
				this.users = data;
				this.status.fetchUsers = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchUsers = Status.Failure;
			});
			throw e;
		}
	}

	@action
	async fetchUsersByIds(userIds: string[]): Promise<void> {
		if (!userIds.length) {
			runInAction(() => {
				this.assignmentUsers = [];
			});
			return;
		}
		runInAction(() => {
			this.status.fetchUsersByIds = Status.Pending;
		});
		try {
			const ids = userIds.join(',');
			const { data } = await directoryAxiosClient.get<DirectoryUser[]>(
				`${DIRECTORY_API.USERS}?id=${ids}&include_group_tree=false`
			);
			runInAction(() => {
				this.assignmentUsers = data;
				this.status.fetchUsersByIds = Status.Success;
			});
		} catch (e) {
			runInAction(() => {
				this.status.fetchUsersByIds = Status.Failure;
			});
			throw e;
		}
	}
}

export const assignUsersStore = new AssignUsersStore();
export default AssignUsersStore;
